import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ChartLine,
  Table,
  FilePdf,
  FileXls,
  Clock,
  Gift,
  X,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { formatINR } from "../../lib/loanMath";
import LoanChartStudio from "./charts/LoanChartStudio";

const todayISO = () => new Date().toISOString().slice(0, 10);

const SOURCES = [
  "Annual Bonus",
  "Incentive",
  "FD/Investment Maturity",
  "Asset Sale",
  "Inheritance/Gift",
  "Other",
];

/**
 * ProgressReports — Feature 5.
 * READ-ONLY tab: progress tracking + Bulk Payment Report (PDF/Excel).
 */
export default function ProgressReports({ loans, userName, planName }) {
  const [view, setView] = useState("individual");         // individual | cumulative
  const [display, setDisplay] = useState("amounts");       // amounts | graphs
  const [asOn, setAsOn] = useState(todayISO());
  const [grouping, setGrouping] = useState("fy");          // monthly | cy | fy
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const chartReportRef = useRef(null);

  const activeLoans = useMemo(() =>
    loans.filter((l) => Number(l.current_balance || l.principal) > 0), [loans]);

  const fetchData = useCallback(async () => {
    if (!activeLoans.length) { setData(null); return; }
    setLoading(true);
    try {
      if (view === "individual") {
        const loan = activeLoans[selectedIdx] || activeLoans[0];
        const { data } = await api.post("/loans/progress/compute", {
          loan, as_on: asOn, grouping,
        });
        setData(data);
      } else {
        const { data } = await api.post("/loans/progress/portfolio", {
          loans: activeLoans, as_on: asOn, grouping,
        });
        setData(data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not compute progress");
    } finally {
      setLoading(false);
    }
  }, [view, selectedIdx, asOn, grouping, activeLoans]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const downloadReport = useCallback(async (fmt) => {
    try {
      const body = { view, as_on: asOn, grouping, loans: activeLoans, user_name: userName || "" };
      if (display === "graphs" && chartReportRef.current) body.chart = await chartReportRef.current.build();
      const r = await api.post(
        `/loans/progress-report/${fmt}`,
        body,
        { responseType: "blob" }
      );
      const blob = new Blob([r.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Estima_Loan_Progress_${asOn}.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the report. Please try again.");
    }
  }, [view, asOn, grouping, activeLoans, userName, display]);

  if (!activeLoans.length) {
    return (
      <div className="card-flat p-10 text-center" data-testid="progress-empty">
        <ChartLine size={32} weight="duotone" className="mx-auto mb-2 text-muted-foreground" />
        Add or upload loans to start tracking progress.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="progress-reports-panel">
      {/* Toolbar */}
      <div className="card-flat p-4 flex flex-wrap items-center gap-3">
        <ToggleGroup
          value={view}
          onChange={setView}
          options={[
            { v: "individual", label: "Individual" },
            { v: "cumulative", label: "Cumulative" },
          ]}
          testid="progress-toggle-view"
        />
        <ToggleGroup
          value={display}
          onChange={setDisplay}
          options={[
            { v: "amounts", label: "Amounts", icon: <Table size={11} /> },
            { v: "graphs", label: "Graphs", icon: <ChartLine size={11} /> },
          ]}
          testid="progress-toggle-display"
        />
        {view === "individual" && (
          <select
            value={selectedIdx}
            onChange={(e) => setSelectedIdx(Number(e.target.value))}
            className="input-dark px-2 py-1.5 text-xs"
            data-testid="progress-select-loan"
          >
            {activeLoans.map((l, i) => <option key={l._k || l.name} value={i}>{l.name}</option>)}
          </select>
        )}
        <label className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
          As on
          <input type="date" value={asOn} onChange={(e) => setAsOn(e.target.value)}
                 className="input-dark px-2 py-1 text-xs ml-1" data-testid="progress-input-ason" />
        </label>
        <select value={grouping} onChange={(e) => setGrouping(e.target.value)}
                className="input-dark px-2 py-1.5 text-xs" data-testid="progress-select-grouping">
          <option value="monthly">Monthly</option>
          <option value="cy">Calendar year</option>
          <option value="fy">Financial year (Apr–Mar)</option>
        </select>
        <div className="ml-auto flex gap-1">
          <button onClick={() => downloadReport("pdf")} className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1 border hairline rounded" data-testid="progress-download-pdf">
            <FilePdf size={11} weight="duotone" /> PDF
          </button>
          <button onClick={() => downloadReport("xlsx")} className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1 border hairline rounded" data-testid="progress-download-xlsx">
            <FileXls size={11} weight="duotone" /> Excel
          </button>
          <button onClick={() => setBulkOpen(true)} className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid="progress-bulk-report-btn">
            <Gift size={11} weight="duotone" /> Bulk Payment Report
          </button>
        </div>
      </div>

      {loading && <div className="text-xs text-muted-foreground">Loading…</div>}

      {data && view === "individual" && display === "amounts" && data.paid && (
        <IndividualAmounts data={data} />
      )}

      {data && view === "cumulative" && display === "amounts" && data.totals && (
        <CumulativeAmounts data={data} />
      )}
      {display === "graphs" && (
        <LoanChartStudio loans={activeLoans} asOn={asOn} planName={planName} view={view} setView={setView}
                         selectedIdx={selectedIdx} setSelectedIdx={setSelectedIdx} reportRef={chartReportRef}>
          {data && view === "individual" && data.paid && <IndividualGraphs data={data} />}
          {data && view === "cumulative" && data.totals && <CumulativeGraphs data={data} />}
        </LoanChartStudio>
      )}

      {data?.has_full_history === false && (
        <div className="text-[11px] text-muted-foreground flex items-start gap-2">
          <Clock size={11} weight="duotone" className="shrink-0 mt-0.5" />
          Add your loan start date and original amount in My Loans to see full history. Past figures assume the current state.
        </div>
      )}
      <div className="text-[10px] text-muted-foreground">
        Past figures are <b>Estimated</b> assuming EMIs were paid on time. For planning only, not financial advice.
      </div>

      <BulkPaymentReportDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        loans={activeLoans}
        userName={userName}
      />
    </div>
  );
}

// ===========================================================================
// Toolbar — ToggleGroup
// ===========================================================================
function ToggleGroup({ value, onChange, options, testid }) {
  return (
    <div className="inline-flex gap-0.5 border hairline rounded p-0.5" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          className={`px-2 py-1 text-[11px] inline-flex items-center gap-1 rounded ${
            value === o.v ? "bg-[hsl(var(--primary))] text-background" : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid={`${testid}-${o.v}`}
        >
          {o.icon}{o.label}
        </button>
      ))}
    </div>
  );
}

// ===========================================================================
// Individual — Amounts & Graphs
// ===========================================================================
function IndividualAmounts({ data }) {
  const p = data.paid; const f = data.future;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="progress-cards">
        <Card label="Principal paid" value={formatINR(p.principal)} />
        <Card label="Interest paid" value={formatINR(p.interest)} />
        <Card label="Outstanding" value={formatINR(data.outstanding_now)} />
        <Card label="Interest to pay" value={formatINR(f.interest)} />
        <Card label="Total paid" value={formatINR(p.total_paid)} />
        <Card label="EMIs paid / left" value={`${p.emis_paid} / ${f.emis_left}`} />
        <Card label="Closure date" value={f.closure_date || "—"} />
        <Card label="% complete" value={`${data.progress.pct_principal_repaid.toFixed(2)}%`} />
      </div>
      <PeriodTable periods={data.periods} />
    </div>
  );
}

function CumulativeAmounts({ data }) {
  const t = data.totals;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="progress-cards">
        <Card label="Principal paid" value={formatINR(t.paid_principal)} />
        <Card label="Interest paid" value={formatINR(t.paid_interest)} />
        <Card label="Outstanding" value={formatINR(t.outstanding_now)} />
        <Card label="Interest to pay" value={formatINR(t.future_interest)} />
        <Card label="Total paid" value={formatINR(t.paid_total)} />
        <Card label="Total to pay" value={formatINR(t.future_total)} />
        <Card label="Monthly EMI" value={`${formatINR(t.emi_now)}/mo`} />
        <Card label="Debt-free date" value={t.debt_free_date || "—"} />
      </div>
      <div className="card-flat p-4 overflow-x-auto">
        <div className="eyebrow mb-2 text-[9px]">Per-loan</div>
        <table className="w-full text-[11px] min-w-[720px]" data-testid="progress-per-loan-table">
          <thead>
            <tr className="text-left text-muted-foreground">
              <Th>Loan</Th><Th>Original</Th><Th>Principal paid</Th><Th>Interest paid</Th>
              <Th>Outstanding</Th><Th>Interest to pay</Th><Th>% complete</Th><Th>Closure</Th>
            </tr>
          </thead>
          <tbody>
            {data.per_loan.map((l) => (
              <tr key={l.loan_name} className="border-t hairline">
                <Td><b>{l.loan_name}</b></Td>
                <Td>{formatINR(l.original_amount, true)}</Td>
                <Td>{formatINR(l.paid.principal, true)}</Td>
                <Td>{formatINR(l.paid.interest, true)}</Td>
                <Td>{formatINR(l.outstanding_now, true)}</Td>
                <Td>{formatINR(l.future.interest, true)}</Td>
                <Td>{l.progress.pct_principal_repaid.toFixed(2)}%</Td>
                <Td>{l.future.closure_date || "—"}</Td>
              </tr>
            ))}
            <tr className="border-t-2 border-foreground font-semibold">
              <Td>Total</Td>
              <Td>{formatINR(t.original_amount, true)}</Td>
              <Td>{formatINR(t.paid_principal, true)}</Td>
              <Td>{formatINR(t.paid_interest, true)}</Td>
              <Td>{formatINR(t.outstanding_now, true)}</Td>
              <Td>{formatINR(t.future_interest, true)}</Td>
              <Td>{t.pct_principal_repaid.toFixed(2)}%</Td>
              <Td>{t.debt_free_date || "—"}</Td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PeriodTable({ periods }) {
  if (!periods?.length) return (
    <div className="text-[11px] text-muted-foreground">
      Period breakdown needs a loan start date — add it in My Loans.
    </div>
  );
  return (
    <div className="card-flat p-4 overflow-x-auto">
      <div className="eyebrow mb-2 text-[9px]">By period</div>
      <table className="w-full text-[11px] min-w-[640px]" data-testid="progress-period-table">
        <thead>
          <tr className="text-left text-muted-foreground">
            <Th>Period</Th><Th>Opening</Th><Th>EMI paid</Th><Th>Principal</Th>
            <Th>Interest</Th><Th>Prepayment</Th><Th>Closing</Th><Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          {periods.map((p) => (
            <tr key={p.label} className={`border-t hairline ${p.status === "Paid" ? "" : "text-muted-foreground"}`}>
              <Td><b>{p.label}</b></Td>
              <Td>{formatINR(p.opening, true)}</Td>
              <Td>{formatINR(p.emi_paid, true)}</Td>
              <Td>{formatINR(p.principal, true)}</Td>
              <Td>{formatINR(p.interest, true)}</Td>
              <Td>{p.prepayment ? formatINR(p.prepayment, true) : "—"}</Td>
              <Td>{formatINR(p.closing, true)}</Td>
              <Td>
                <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                  p.status === "Paid" ? "bg-[hsl(var(--secondary))]/15 text-[hsl(var(--secondary))]"
                  : p.status === "Partial" ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"
                                      : "bg-muted/30"
                }`}>{p.status}</span>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===========================================================================
// Graphs
// ===========================================================================
function IndividualGraphs({ data }) {
  return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        <DonutCard title="Principal vs Interest" segments={[
          { label: "Principal paid", value: data.paid.principal, color: "hsl(var(--secondary))" },
          { label: "Interest paid",  value: data.paid.interest,  color: "hsl(var(--primary))" },
          { label: "Principal to pay", value: data.future.principal, color: "hsl(var(--secondary) / 0.35)" },
          { label: "Interest to pay",  value: data.future.interest,  color: "hsl(var(--primary) / 0.35)" },
        ]} />
        <ProgressRingCard pct={data.progress.pct_principal_repaid}
                           paid={data.paid.principal} total={data.original_amount} />
      </div>
      <BalanceOverTimeChart timeline={data.timeline} />
      <StackedBars periods={data.periods} />
    </div>
  );
}

function CumulativeGraphs({ data }) {
  const t = data.totals;
  return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        <DonutCard title="Portfolio split" segments={[
          { label: "Principal paid", value: t.paid_principal, color: "hsl(var(--secondary))" },
          { label: "Interest paid",  value: t.paid_interest,  color: "hsl(var(--primary))" },
          { label: "Principal to pay", value: t.future_principal, color: "hsl(var(--secondary) / 0.35)" },
          { label: "Interest to pay",  value: t.future_interest,  color: "hsl(var(--primary) / 0.35)" },
        ]} />
        <ProgressRingCard pct={t.pct_principal_repaid} paid={t.paid_principal} total={t.original_amount} />
      </div>
      <div className="card-flat p-4">
        <div className="eyebrow mb-2 text-[9px]">Progress per loan</div>
        <div className="space-y-2">
          {data.per_loan.map((l) => (
            <div key={l.loan_name} data-testid={`progress-bar-${l.loan_name}`}>
              <div className="flex justify-between text-[11px]">
                <span>{l.loan_name}</span>
                <span className="text-muted-foreground">{l.progress.pct_principal_repaid.toFixed(1)}%</span>
              </div>
              <div className="h-2 bg-muted/30 rounded overflow-hidden mt-1">
                <div className="h-full bg-[hsl(var(--secondary))]" style={{ width: `${Math.min(100, l.progress.pct_principal_repaid)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Card({ label, value }) {
  return (
    <div className="card-flat p-3">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="num-metric text-sm mt-0.5">{value}</div>
    </div>
  );
}
function Th({ children }) { return <th className="py-1 pr-3 font-normal">{children}</th>; }
function Td({ children }) { return <td className="py-1 pr-3">{children}</td>; }

function DonutCard({ title, segments }) {
  const total = segments.reduce((s, x) => s + Math.max(0, x.value || 0), 0) || 1;
  const C = 2 * Math.PI * 42;
  let offset = 0;
  return (
    <div className="card-flat p-4" data-testid="progress-donut">
      <div className="eyebrow mb-2 text-[9px]">{title}</div>
      <div className="flex items-center gap-4">
        <svg viewBox="0 0 100 100" className="w-28 h-28">
          <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--muted) / 0.3)" strokeWidth="12" />
          {segments.map((s) => {
            const frac = Math.max(0, s.value || 0) / total;
            const dash = frac * C;
            const el = (
              <circle key={s.label} cx="50" cy="50" r="42" fill="none"
                       stroke={s.color} strokeWidth="12"
                       strokeDasharray={`${dash} ${C - dash}`}
                       strokeDashoffset={-offset}
                       transform="rotate(-90 50 50)" />
            );
            offset += dash;
            return el;
          })}
        </svg>
        <ul className="text-[11px] space-y-0.5">
          {segments.map((s) => (
            <li key={s.label} className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2" style={{ background: s.color }} />
              {s.label}: {formatINR(s.value, true)}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ProgressRingCard({ pct, paid, total }) {
  const p = Math.max(0, Math.min(100, pct || 0));
  const C = 2 * Math.PI * 42;
  const dash = (p / 100) * C;
  return (
    <div className="card-flat p-4" data-testid="progress-ring">
      <div className="eyebrow mb-2 text-[9px]">Portfolio complete</div>
      <div className="flex items-center gap-4">
        <svg viewBox="0 0 100 100" className="w-28 h-28">
          <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--muted) / 0.3)" strokeWidth="10" />
          <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--secondary))" strokeWidth="10"
                   strokeDasharray={`${dash} ${C - dash}`} transform="rotate(-90 50 50)" strokeLinecap="round" />
          <text x="50" y="54" textAnchor="middle" fontSize="15" fontWeight="700" fill="currentColor">
            {p.toFixed(0)}%
          </text>
        </svg>
        <div className="text-[11px]">
          <div>Paid {formatINR(paid, true)}</div>
          <div className="text-muted-foreground">of {formatINR(total, true)}</div>
        </div>
      </div>
    </div>
  );
}

function BalanceOverTimeChart({ timeline }) {
  if (!timeline?.length) return null;
  const maxBal = Math.max(...timeline.map((t) => t.balance), 1);
  const maxMo = Math.max(...timeline.map((t) => t.month), 1);
  const W = 480, H = 160, pad = 24;
  const px = (m) => pad + (W - 2 * pad) * (m / maxMo);
  const py = (b) => H - pad - (H - 2 * pad) * (b / maxBal);
  const past = timeline.filter((t) => t.is_past);
  const future = timeline.filter((t) => !t.is_past);
  const pathStr = (arr) => arr.map((t, i) => `${i === 0 ? "M" : "L"} ${px(t.month).toFixed(1)} ${py(t.balance).toFixed(1)}`).join(" ");
  const todayX = past.length ? px(past[past.length - 1].month) : null;
  return (
    <div className="card-flat p-4" data-testid="progress-balance-chart">
      <div className="eyebrow mb-2 text-[9px] flex items-center gap-1">
        <ChartLine size={11} weight="duotone" /> Outstanding balance over time
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40">
        <path d={pathStr(past)} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" />
        <path d={pathStr(future)} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.75" strokeDasharray="4 4" strokeOpacity="0.6" />
        {todayX !== null && (
          <>
            <line x1={todayX} y1={pad} x2={todayX} y2={H - pad} stroke="hsl(var(--secondary))" strokeWidth="1" strokeDasharray="2 2" />
            <text x={todayX + 3} y={pad + 10} fontSize="9" fill="hsl(var(--secondary))">Today</text>
          </>
        )}
      </svg>
      <div className="flex gap-4 text-[10px] text-muted-foreground mt-1">
        <span><span className="inline-block w-3 h-0.5 bg-[hsl(var(--primary))] align-middle" /> Past</span>
        <span><span className="inline-block w-3 h-0.5 border-t-2 border-dashed border-[hsl(var(--primary))] align-middle" /> Future</span>
      </div>
    </div>
  );
}

function StackedBars({ periods }) {
  if (!periods?.length) return null;
  const take = periods.slice(0, 24);
  const maxVal = Math.max(...take.map((p) => p.principal + p.interest), 1);
  return (
    <div className="card-flat p-4 overflow-x-auto" data-testid="progress-stacked-bars">
      <div className="eyebrow mb-2 text-[9px]">Principal vs Interest by period</div>
      <div className="flex items-end gap-1 h-32 min-w-fit">
        {take.map((p) => {
          const hP = (p.principal / maxVal) * 100;
          const hI = (p.interest / maxVal) * 100;
          const faded = p.status !== "Paid" ? "opacity-50" : "";
          return (
            <div key={p.label} className={`flex flex-col items-center text-[8px] ${faded}`}>
              <div className="flex flex-col-reverse w-6">
                <div className="bg-[hsl(var(--secondary))]" style={{ height: `${hP}px` }} title={`Principal ${formatINR(p.principal)}`} />
                <div className="bg-[hsl(var(--primary))]" style={{ height: `${hI}px` }} title={`Interest ${formatINR(p.interest)}`} />
              </div>
              <div className="rotate-45 origin-left translate-x-2 mt-2 whitespace-nowrap text-muted-foreground">{p.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===========================================================================
// Bulk Payment Report dialog
// ===========================================================================
function BulkPaymentReportDialog({ open, onClose, loans, userName }) {
  const [source, setSource] = useState("Annual Bonus");
  const [sourceDetail, setSourceDetail] = useState("");
  const [amount, setAmount] = useState(500000);
  const [eventDate, setEventDate] = useState(todayISO());
  const [allocations, setAllocations] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // Default allocation: highest-rate first, waterfall
  useEffect(() => {
    if (!open || !loans?.length) return;
    let left = Number(amount) || 0;
    const sorted = [...loans].sort((a, b) => (b.interest_rate_pct || 0) - (a.interest_rate_pct || 0));
    const next = [];
    for (const l of sorted) {
      if (left <= 0) break;
      const bal = Number(l.current_balance || l.principal) || 0;
      if (bal <= 0) continue;
      const give = Math.min(bal, left);
      next.push({ name: l.name, amount: give, charge_pct: 0, option: "reduce_tenure" });
      left -= give;
    }
    setAllocations(next);
  }, [open, loans, amount]);

  // Debounced preview — all inputs collapsed into a single memoised payload so
  // the dep list stays small and the effect doesn't fire on identical refs.
  const previewPayload = useMemo(() => ({
    loans, event_date: eventDate, source, source_detail: sourceDetail,
    bonus_amount: Number(amount) || 0, allocations, user_name: userName || "",
  }), [loans, eventDate, source, sourceDetail, amount, allocations, userName]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/loans/bulk-payment-report/compute", previewPayload);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Preview failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, previewPayload]);

  const download = async (fmt) => {
    try {
      const r = await api.post(
        `/loans/bulk-payment-report/${fmt}`,
        { loans, event_date: eventDate, source, source_detail: sourceDetail,
          bonus_amount: Number(amount) || 0, allocations, user_name: userName || "" },
        { responseType: "blob" }
      );
      const blob = new Blob([r.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Estima_Bulk_Payment_Report_${eventDate}.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the report.");
    }
  };

  const updateAlloc = (idx, patch) => {
    setAllocations((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[10020] flex items-start sm:items-center justify-center p-2 sm:p-4" data-testid="bulk-payment-dialog">
      <button className="absolute inset-0 bg-black/70" onClick={onClose} aria-label="Close" />
      <div className="relative card-flat w-full max-w-4xl p-5 sm:p-6 max-h-[95vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
                data-testid="bulk-payment-close">
          <X size={16} weight="bold" />
        </button>
        <div className="eyebrow mb-1 flex items-center gap-2">
          <Gift size={14} weight="duotone" /> Bulk Payment Report
        </div>
        <h2 className="font-serif text-2xl mb-4">Before vs After for any bulk payment</h2>

        <div className="grid sm:grid-cols-4 gap-3 mb-4">
          <label>
            <div className="eyebrow text-[9px] mb-1">Source</div>
            <select value={source} onChange={(e) => setSource(e.target.value)}
                    className="input-dark w-full px-2 py-1.5 text-xs" data-testid="bulk-source">
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="sm:col-span-2">
            <div className="eyebrow text-[9px] mb-1">Note (optional)</div>
            <input value={sourceDetail} onChange={(e) => setSourceDetail(e.target.value)}
                   placeholder="e.g. Q4 performance bonus" className="input-dark w-full px-2 py-1.5 text-xs"
                   data-testid="bulk-source-detail" />
          </label>
          <label>
            <div className="eyebrow text-[9px] mb-1">Amount (₹)</div>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                   className="input-dark w-full px-2 py-1.5 text-sm" data-testid="bulk-amount" />
          </label>
          <label>
            <div className="eyebrow text-[9px] mb-1">Event date</div>
            <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)}
                   className="input-dark w-full px-2 py-1.5 text-xs" data-testid="bulk-date" />
          </label>
        </div>

        {/* Allocations */}
        <div className="card-flat p-3 mb-4">
          <div className="eyebrow text-[9px] mb-2">Allocate per loan</div>
          <div className="space-y-1">
            {allocations.map((a, i) => (
              <div key={a.name} className="grid grid-cols-[1fr_120px_90px_130px] gap-2 items-center text-xs"
                   data-testid={`bulk-alloc-${a.name}`}>
                <span>{a.name}</span>
                <input type="number" value={a.amount} onChange={(e) => updateAlloc(i, { amount: Number(e.target.value) || 0 })}
                       className="input-dark px-2 py-1 text-xs" placeholder="Amount" />
                <input type="number" step="0.5" value={a.charge_pct}
                       onChange={(e) => updateAlloc(i, { charge_pct: Number(e.target.value) || 0 })}
                       className="input-dark px-2 py-1 text-xs" placeholder="Charge %" />
                <select value={a.option} onChange={(e) => updateAlloc(i, { option: e.target.value })}
                        className="input-dark px-2 py-1 text-xs">
                  <option value="reduce_tenure">Reduce tenure</option>
                  <option value="reduce_emi">Reduce EMI</option>
                </select>
              </div>
            ))}
          </div>
        </div>

        {loading && <div className="text-xs text-muted-foreground mb-3">Computing…</div>}

        {preview && (
          <>
            <div className="card-flat p-4 mb-4" data-testid="bulk-headline">
              <div className="eyebrow mb-1 text-[9px]">Impact</div>
              <p className="text-sm">{preview.headline}</p>
            </div>

            {/* Portfolio before/after */}
            <div className="card-flat p-4 mb-4 overflow-x-auto" data-testid="bulk-portfolio">
              <table className="w-full text-xs min-w-[560px]">
                <thead>
                  <tr className="text-muted-foreground">
                    <Th>Portfolio</Th><Th>Before</Th><Th>After</Th><Th>Saving / Change</Th>
                  </tr>
                </thead>
                <tbody>
                  <Row label="Total outstanding" b={preview.before.portfolio.outstanding} a={preview.after.portfolio.outstanding} tone="save" />
                  <Row label="Monthly EMI" b={preview.before.portfolio.emi} a={preview.after.portfolio.emi} tone="save" />
                  <Row label="Interest to pay" b={preview.before.portfolio.interest_to_pay} a={preview.after.portfolio.interest_to_pay} tone="save" />
                  <tr className="border-t hairline">
                    <Td>Total to pay (incl bonus)</Td>
                    <Td>{formatINR(preview.before.portfolio.total_to_pay, true)}</Td>
                    <Td>{formatINR(preview.after.portfolio.total_to_pay + preview.bonus_amount, true)}</Td>
                    <Td>—</Td>
                  </tr>
                  <tr className="border-t hairline">
                    <Td>Debt-free date</Td>
                    <Td>{preview.before.portfolio.latest_closure || "—"}</Td>
                    <Td>{preview.after.portfolio.latest_closure || "—"}</Td>
                    <Td>{preview.savings.debt_free_unchanged ? "Unchanged" : "Earlier"}</Td>
                  </tr>
                  <tr className="border-t-2 border-foreground font-semibold">
                    <Td>Net saving (interest − charges)</Td><Td /><Td />
                    <Td className="text-[hsl(var(--secondary))]">{formatINR(preview.savings.net_saving, true)}</Td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="flex items-center gap-2 flex-wrap border-t hairline pt-3">
          <button onClick={() => download("pdf")} className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1" data-testid="bulk-download-pdf">
            <FilePdf size={11} weight="duotone" /> Download PDF
          </button>
          <button onClick={() => download("xlsx")} className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1 border hairline rounded" data-testid="bulk-download-xlsx">
            <FileXls size={11} weight="duotone" /> Download Excel
          </button>
          <div className="text-[10px] text-muted-foreground ml-auto">
            A what-if report never changes your loan data.
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, b, a, tone }) {
  const delta = (b || 0) - (a || 0);
  const good = tone === "save" ? delta > 0 : delta < 0;
  const tc = delta !== 0 ? (good ? "text-[hsl(var(--secondary))]" : "text-destructive") : "";
  return (
    <tr className="border-t hairline">
      <Td>{label}</Td>
      <Td>{formatINR(b, true)}</Td>
      <Td>{formatINR(a, true)}</Td>
      <Td className={tc}>{delta >= 0 ? "−" : "+"}{formatINR(Math.abs(delta), true)}</Td>
    </tr>
  );
}
